import { zodResolver } from "@hookform/resolvers/zod";
import { Resources, useResource } from "Translations/Resources";
import {
  TRANSACTION_PUBLIC_ID_LENGTH,
  TRANSACTION_PUBLIC_ID_PATTERN,
} from "Utils/TransactionUtils";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const RESOURCES = Resources.Customer.Pay.ManualEntry;

const normalizeTransactionPublicID = (value: string) =>
  value.replace(/\s/g, "").toUpperCase();

/**
 * Shape of a transaction public ID, checked without a lookup: a wrong length or
 * an impossible character is reported inline by the form, while an unknown ID of
 * the right shape is left to the screen the form navigates to.
 */
const getTransactionIdEntrySchema = (invalidIdMessage: string) =>
  z.object({
    transactionPublicID: z
      .string()
      .transform(normalizeTransactionPublicID)
      .refine(
        (value) =>
          value.length === TRANSACTION_PUBLIC_ID_LENGTH &&
          TRANSACTION_PUBLIC_ID_PATTERN.test(value),
        invalidIdMessage,
      ),
  });

export type TransactionIdEntryFormData = z.infer<
  ReturnType<typeof getTransactionIdEntrySchema>
>;

/**
 * Form state behind TransactionIdEntryForm. Split out so a caller can drive its
 * own validation off what is being typed — the send-payment screen looks the ID
 * up live — without re-deriving the normalization and length rules.
 */
export const useTransactionIdEntryForm = () => {
  const { t } = useResource();

  const schema = useMemo(
    () => getTransactionIdEntrySchema(t(RESOURCES.InvalidId)),
    [t],
  );

  const { control, handleSubmit } = useForm<TransactionIdEntryFormData>({
    resolver: zodResolver(schema),
  });

  const value = useWatch({ control, name: "transactionPublicID" });
  // Same normalization the schema applies, so callers compare against the value
  // that submitting would actually produce.
  const transactionPublicID = normalizeTransactionPublicID(value ?? "");

  return {
    control,
    handleSubmit,
    transactionPublicID,
    isEmpty: transactionPublicID.length === 0,
    hasExactLength: transactionPublicID.length === TRANSACTION_PUBLIC_ID_LENGTH,
    isTooLong: transactionPublicID.length > TRANSACTION_PUBLIC_ID_LENGTH,
  };
};
